import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import {
  listPurchaseRequestsApi,
  updatePurchaseRequestStatusApi,
  PurchaseRequestItem,
} from "../../features/purchase-request/api/purchase-request.api"
import {
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  ShoppingBagIcon,
  ChatBubbleLeftIcon,
  CubeIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

const LIMIT = 20

const STATUS_CONFIG: Record<
  PurchaseRequestItem["status"],
  { label: string; className: string; dot: string; gradient: string }
> = {
  pending:  { label: "В ожидании",  className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",   dot: "bg-amber-400",   gradient: "from-amber-500 to-yellow-400" },
  paid:     { label: "Оплачен",     className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", dot: "bg-emerald-400", gradient: "from-emerald-500 to-green-400" },
  rejected: { label: "Отклонён", className: "bg-red-50 text-red-700 ring-1 ring-red-200",          dot: "bg-red-400",     gradient: "from-red-500 to-rose-400" },
  canceled: { label: "Отменён", className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",      dot: "bg-gray-400",    gradient: "from-gray-400 to-gray-300" },
}

const StatusBadge = ({ status }: { status: PurchaseRequestItem["status"] }) => {
  const cfg = STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  )
}

export const PurchaseRequestsPage = () => {
  const [items, setItems] = useState<PurchaseRequestItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const [selected, setSelected] = useState<PurchaseRequestItem | null>(null)
  const [newStatus, setNewStatus] = useState<PurchaseRequestItem["status"]>("pending")
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const res = await listPurchaseRequestsApi(LIMIT, (page - 1) * LIMIT)
      setItems(res.data.items)
      setTotal(res.data.total)
      setLoading(false)
    }
    load()
  }, [page])

  const totalPages = Math.ceil(total / LIMIT)
  const from = (page - 1) * LIMIT + 1
  const to = Math.min(page * LIMIT, total)

  const openDrawer = (r: PurchaseRequestItem) => {
    setSelected(r)
    setNewStatus(r.status)
    setSaveOk(false)
  }

  const handleSave = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await updatePurchaseRequestStatusApi(selected.id, newStatus)
      setItems((prev) => prev.map((i) => (i.id === selected.id ? { ...i, status: newStatus } : i)))
      setSelected((prev) => prev ? { ...prev, status: newStatus } : null)
      setSaveOk(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <DashboardLayout title="Заявки на покупку">
      <div className="space-y-4">

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {(["pending", "paid", "rejected", "canceled"] as const).map((s) => {
            const count = items.filter((i) => i.status === s).length
            const cfg = STATUS_CONFIG[s]
            return (
              <div key={s} className="bg-white rounded-xl border px-4 py-3 flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                <div>
                  <p className="text-xs text-gray-400">{cfg.label}</p>
                  <p className="text-lg font-semibold text-gray-800">{count}</p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50/60">
            <div className="flex items-center gap-2">
              <ShoppingBagIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Все заявки</span>
              {total > 0 && (
                <span className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full">{total}</span>
              )}
            </div>
            {total > 0 && (
              <span className="text-xs text-gray-400">{from}–{to} of {total}</span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><EnvelopeIcon className="w-3.5 h-3.5" />Email</span>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><PhoneIcon className="w-3.5 h-3.5" />Телефон</span>
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Telegram</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Цель</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><ClockIcon className="w-3.5 h-3.5" />Статус</span>
                  </th>
                  <th className="px-5 py-3 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {[1,2,3,4,5,6].map((j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-gray-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <ShoppingBagIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Заявок на покупку пока нет</p>
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.id} onClick={() => openDrawer(r)} className="hover:bg-gray-50/60 transition-colors cursor-pointer group">
                      <td className="px-5 py-3.5 font-medium text-gray-800">{r.email}</td>
                      <td className="px-5 py-3.5 text-gray-600">{r.phone_number}</td>
                      <td className="px-5 py-3.5">
                        {r.telegram
                          ? <span className="text-blue-500">@{r.telegram.replace(/^@/, "")}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-md font-medium">
                          {r.target_type}
                          <span className="text-blue-400">/</span>
                          <span className="font-mono">{r.target_id.slice(0, 8)}…</span>
                        </span>
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); openDrawer(r) }}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                        >
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Обновить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && total > 0 && (
            <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
              <span className="text-xs text-gray-400">
                Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
                <span className="font-medium text-gray-600">{totalPages || 1}</span>
              </span>
              <div className="flex gap-1.5">
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Назад
                </button>
                <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Вперёд →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${selected ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setSelected(null)}
          />
          <div
            className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${selected ? "translate-x-0" : "translate-x-full"}`}
          >
            {selected && (
              <>
                {/* Header */}
                <div className={`bg-gradient-to-r ${STATUS_CONFIG[selected.status].gradient} px-6 pt-6 pb-8 flex-shrink-0 relative`}>
                  <button onClick={() => setSelected(null)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                    <XMarkIcon className="w-4 h-4 text-white" />
                  </button>

                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingBagIcon className="w-5 h-5 text-white/80" />
                    <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Заявка на покупку</span>
                  </div>

                  <p className="text-white font-semibold text-lg pr-10 truncate">{selected.email}</p>

                  <div className="mt-3">
                    <StatusBadge status={selected.status} />
                  </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                  {/* Contact info */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Контакты</p>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <EnvelopeIcon className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <span className="text-sm text-gray-700">{selected.email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <PhoneIcon className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-700">{selected.phone_number}</span>
                    </div>
                    {selected.telegram && (
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                          <ChatBubbleLeftIcon className="w-3.5 h-3.5 text-sky-600" />
                        </div>
                        <span className="text-sm text-blue-500">@{selected.telegram.replace(/^@/, "")}</span>
                      </div>
                    )}
                  </div>

                  {/* Target */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Цель</p>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <CubeIcon className="w-3.5 h-3.5 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 capitalize">{selected.target_type}</p>
                        <p className="font-mono text-xs text-gray-600">{selected.target_id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status picker */}
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Изменить статус</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(["pending", "paid", "rejected", "canceled"] as const).map((s) => {
                        const cfg = STATUS_CONFIG[s]
                        const active = newStatus === s
                        return (
                          <button key={s} onClick={() => setNewStatus(s)}
                            className={`flex items-center gap-2 px-3 py-3 rounded-xl border text-sm font-medium transition-all ${
                              active
                                ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                            }`}>
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? "bg-white" : cfg.dot}`} />
                            {cfg.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {saveOk && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                      Статус обновлён
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
                  <button onClick={() => setSelected(null)}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors">
                    Закрыть
                  </button>
                  <button disabled={saving} onClick={handleSave}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm shadow-blue-200">
                    {saving
                      ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <CheckCircleIcon className="w-4 h-4" />}
                    Сохранить
                  </button>
                </div>
              </>
            )}
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}
